<!-- Score, streak and whatever third number the mode cares about.

     One HUD for every mode so the numbers never move between screens. The
     third slot is a prop rather than a component per mode: Last Gasp wants
     seconds left, Chain Reaction wants chain length, Endurance wants the stage
     it has climbed to.

     Lives are their own slot rather than a number in that third one. A spent
     life is greyed rather than removed so the row never shifts width mid-game,
     which would drag the eye away from the question.

     THE NUMBERS USED TO CHANGE WITHOUT ANYONE NOTICING. Every value in here
     was swapped between frames with no transition of any kind — including the
     heart, which went to 0.22 opacity in a single frame. Losing a life is the
     second biggest event in this game and it was rendered at exactly the same
     weight as the score ticking over by forty, which is to say at no weight at
     all.

     So three transient classes, each set by a watcher and cleared by a timer:

       · `bumped` on the score, and on the streak, when they go UP. Nothing
         when they go down. A broken streak is already being shouted about by
         the verdict flash a few pixels below, and two things moving on the
         same beat is how a HUD becomes noise.
       · `lit` on the flame the moment the streak crosses three and the flame
         first appears.
       · `breaking` on the ONE heart that was just spent. Not on hearts that
         were already gone when the screen mounted — a mode entered on two
         lives must not replay somebody else's death — which is why it is
         keyed to the index that changed rather than to `spent`.

     The `lives` contract is untouched: three hearts, spent ones at 0.22, and
     `lives === -1` hides the slot for the modes that have none. -->
<template>
  <div class="hud">
    <div class="stat" :class="{ bumped: scoreBump }">
      <span class="n tabular">{{ score.toLocaleString() }}</span>
      <span class="k">Score</span>
    </div>

    <div class="stat" :class="{ bumped: streakBump }">
      <span class="n tabular">
        {{ streak }}<span
          v-if="streak >= 3"
          class="flame"
          :class="{ lit: flameLit }"
          aria-hidden="true"
        >&nbsp;🔥</span>
      </span>
      <span class="k">Streak</span>
    </div>

    <div v-if="label" class="stat">
      <span class="n tabular">{{ value }}</span>
      <span class="k">{{ label }}</span>
    </div>

    <div
      v-if="lives >= 0"
      class="stat"
      role="status"
      :aria-label="`${lives} of ${maxLives} lives left`"
    >
      <span class="n hearts" aria-hidden="true">
        <span
          v-for="i in maxLives"
          :key="i"
          class="heart"
          :class="{ spent: i > lives, breaking: i === breaking }"
        >♥</span>
      </span>
      <span class="k">Lives</span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  score: { type: Number, default: 0 },
  streak: { type: Number, default: 0 },
  label: { type: String, default: '' },
  value: { type: [String, Number], default: '' },
  // -1 means this mode has no lives at all, so the slot stays hidden.
  lives: { type: Number, default: -1 },
  maxLives: { type: Number, default: 3 },
});

const scoreBump = ref(false);
const streakBump = ref(false);
const flameLit = ref(false);
const breaking = ref(-1);

const timers = { score: null, streak: null, flame: null, heart: null };

/**
 * Set a flag, then clear it after the animation.
 *
 * It goes false-then-true across a frame because two correct answers inside
 * 400ms is completely normal in Endurance, and re-adding a class that is
 * already there restarts nothing — the second hit would be silent, which is
 * precisely the case where the feedback matters most.
 */
function pulse(flag, key, ms) {
  flag.value = false;
  const raise = () => { flag.value = true; };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(raise);
  else raise();
  clearTimeout(timers[key]);
  timers[key] = setTimeout(() => { flag.value = false; }, ms);
}

watch(() => props.score, (now, was) => {
  if (!(now > was)) return;
  pulse(scoreBump, 'score', 420);
});

watch(() => props.streak, (now, was) => {
  if (!(now > was)) return;
  pulse(streakBump, 'streak', 420);
  // The flame only ignites on the crossing, not on every streak tick above it.
  if (now >= 3 && was < 3) pulse(flameLit, 'flame', 540);
});

watch(() => props.lives, (now, was) => {
  // Guarded hard: a mode with no lives passes -1 forever, and a mode being set
  // up passes its starting count once. Neither is a death.
  if (typeof was !== 'number' || was < 0 || now < 0 || now >= was) return;
  breaking.value = was;
  clearTimeout(timers.heart);
  timers.heart = setTimeout(() => { breaking.value = -1; }, 640);
});

onUnmounted(() => Object.values(timers).forEach(clearTimeout));
</script>
